import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { apiClient } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  ShieldCheck, Users, Home, ArrowLeftRight, FolderTree, PiggyBank, Target, CalendarClock,
  RefreshCw, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const StatCard = ({ icon: Icon, label, value, accent = 'text-primary' }) => (
  <Card>
    <CardContent className="pt-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${accent}`} strokeWidth={1.5} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        apiClient.get('/admin/stats'),
        apiClient.get('/admin/users'),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Solo gli account admin possono accedere');
        navigate('/');
      } else {
        toast.error('Errore caricamento');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Lock className="w-12 h-12 text-muted-foreground mb-3" strokeWidth={1.5} />
        <h2 className="text-xl font-semibold mb-1">Area riservata</h2>
        <p className="text-muted-foreground">Questa pagina è disponibile solo agli account admin.</p>
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <Badge className="bg-primary/10 text-primary border-0">ADMIN</Badge>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Pannello di controllo</h1>
          <p className="text-muted-foreground">Stato generale di sistema · {user?.name}</p>
        </div>
        <Button variant="outline" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users}          label="Utenti"        value={stats.totals.users}        />
        <StatCard icon={Home}           label="Famiglie"      value={stats.totals.families}     />
        <StatCard icon={ArrowLeftRight} label="Transazioni"   value={stats.totals.transactions} accent="text-emerald-600 dark:text-emerald-400" />
        <StatCard icon={FolderTree}     label="Categorie"     value={stats.totals.categories}   />
        <StatCard icon={PiggyBank}      label="Budget set"    value={stats.totals.budgets}      accent="text-blue-500" />
        <StatCard icon={Target}         label="Obiettivi"     value={stats.totals.goals}        accent="text-orange-500" />
        <StatCard icon={CalendarClock}  label="Ricorrenti"    value={stats.totals.recurring_bills} accent="text-purple-500" />
        <StatCard icon={ShieldCheck}    label="Admin"         value={users.filter((u) => u.role === 'admin').length} />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="hover:shadow-md cursor-pointer transition-all" onClick={() => navigate('/admin/families')}>
          <CardContent className="pt-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Home className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-semibold">Gestione famiglie</p>
              <p className="text-xs text-muted-foreground">Esplora, analizza, esporta report</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent families */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Famiglie recenti</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-2 font-medium">Codice</th>
                  <th className="py-2 pr-2 font-medium">Membri</th>
                  <th className="py-2 pr-2 font-medium">Email</th>
                  <th className="py-2 pr-2 font-medium text-right">Transazioni</th>
                  <th className="py-2 pr-2 font-medium">Creata</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_families.map((f) => (
                  <tr
                    key={f.id}
                    onClick={() => navigate(`/admin/families/${f.id}`)}
                    className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                  >
                    <td className="py-2 pr-2 font-mono">{f.code}</td>
                    <td className="py-2 pr-2 tabular-nums">{f.member_count}</td>
                    <td className="py-2 pr-2 text-muted-foreground truncate max-w-xs">
                      {f.members.map((m) => m.email).join(', ')}
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums font-semibold">{f.transaction_count}</td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">
                      {f.created_at ? new Date(f.created_at).toLocaleDateString('it-IT') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* All users */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Tutti gli utenti ({users.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-2 font-medium">Nome</th>
                  <th className="py-2 pr-2 font-medium">Email</th>
                  <th className="py-2 pr-2 font-medium">Ruolo</th>
                  <th className="py-2 pr-2 font-medium">Family</th>
                  <th className="py-2 pr-2 font-medium">Creato</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 pr-2 font-medium">{u.name}</td>
                    <td className="py-2 pr-2 text-muted-foreground">{u.email}</td>
                    <td className="py-2 pr-2">
                      {u.role === 'admin' ? (
                        <Badge className="bg-primary/10 text-primary border-0">admin</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">{u.role || 'member'}</span>
                      )}
                    </td>
                    <td className="py-2 pr-2 font-mono text-[11px] text-muted-foreground">{u.family_id?.slice(0, 8)}…</td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('it-IT') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPage;
